import { LegacyRef, useCallback, useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { type ApolloError } from '@apollo/client'
import { DataSourceDropdown, SearchInput, TimePicker } from '@ui/components/elements'
import { useAppSearchParams } from '@ui/lib/hooks/useAppSearchParams'
import { useTelemetryStore } from '@ui/stores/explore'
import { useClickAway } from '@uidotdev/usehooks'
import debounce from 'lodash/debounce'

interface SearchBarProps {
  error: ApolloError | undefined
}

export const TelemetrySearchBar = ({ error }: SearchBarProps) => {
  const { searchParams, updateSearchParams } = useAppSearchParams()
  const dropdownRef = useClickAway(() => {
    setDataSourceDropdownOpen(false)
  })

  const { whereClause, setWhereClause, dataSource, setDataSource } = useTelemetryStore(
    useShallow((state) => ({
      whereClause: state.whereClause,
      setWhereClause: state.setWhereClause,
      dataSource: state.dataSource,
      setDataSource: state.setDataSource,
    }))
  )

  const [localWhereClause, setLocalWhereClause] = useState(whereClause)
  const [dataSourceDropdownOpen, setDataSourceDropdownOpen] = useState(false)

  const debouncedUpdate = useCallback(
    debounce(
      (value: string) => {
        setWhereClause(value)
      },
      750,
      { trailing: true }
    ),
    []
  )

  const clearInput = () => {
    setLocalWhereClause('')
    setWhereClause('')
  }

  const setInput = (value: string) => {
    setLocalWhereClause(value)
    debouncedUpdate(value)
  }

  useEffect(() => {
    setLocalWhereClause(whereClause)
  }, [whereClause])

  return (
    <div className="flex w-full items-center justify-between space-x-1 rounded-md border border-gray-200 bg-white p-3">
      <div className="w-fit">
        <DataSourceDropdown
          ref={dropdownRef as LegacyRef<HTMLDivElement>}
          dataSource={dataSource}
          setDataSource={setDataSource}
          dataSourceDropdownOpen={dataSourceDropdownOpen}
          setDataSourceDropdownOpen={setDataSourceDropdownOpen}
        />
      </div>

      <div className="w-full">
        <SearchInput
          inputValue={localWhereClause}
          setInput={setInput}
          dataSource={dataSource}
          error={error}
          clearInput={clearInput}
          searchParams={searchParams}
          updateSearchParams={updateSearchParams}
        />
      </div>

      <div className="w-fit">
        <TimePicker />
      </div>
    </div>
  )
}
